import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '@/hooks/useNotifications';

const ICON_MAP: Record<string, string> = {
  course_approval: 'ri-graduation-cap-line',
  enrollment: 'ri-user-add-line',
  achievement: 'ri-award-line',
  payment: 'ri-secure-payment-line',
  webinar: 'ri-live-line',
  default: 'ri-notification-3-line',
};

const COLOR_MAP: Record<string, string> = {
  course_approval: 'text-yellow-400',
  enrollment: 'text-green-400',
  achievement: 'text-[#D4AF37]',
  payment: 'text-green-400',
  webinar: 'text-[#D4AF37]',
  default: 'text-gray-400',
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'agora';
  if (mins < 60) return `${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotifClick = async (notif: { id: string; is_read: boolean; link: string | null }) => {
    if (!notif.is_read) await markAsRead(notif.id);
    if (notif.link) navigate(notif.link);
    setOpen(false);
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen((p) => !p)}
        className="relative w-9 h-9 flex items-center justify-center rounded-xl bg-white/5 border border-white/[0.08] text-gray-400 hover:text-white hover:bg-white/10 transition-all cursor-pointer"
        aria-label="Notificações"
      >
        <i className="ri-notification-3-line text-sm"></i>
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[9px] font-black rounded-full px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-11 w-80 bg-[#0F0F1A] border border-white/10 rounded-2xl shadow-2xl z-50 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-white/[0.08] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-[#F5F5F0]">Notificações</span>
              {unreadCount > 0 && (
                <span className="text-[9px] font-black text-[#D4AF37] bg-[#D4AF37]/15 px-1.5 py-0.5 rounded-full">
                  {unreadCount} nova{unreadCount > 1 ? 's' : ''}
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllAsRead()}
                className="text-[10px] text-gray-500 hover:text-[#D4AF37] transition-colors cursor-pointer whitespace-nowrap"
              >
                Marcar todas como lidas
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-80 overflow-y-auto divide-y divide-white/5">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <div className="w-10 h-10 flex items-center justify-center bg-white/5 rounded-xl mx-auto mb-2">
                  <i className="ri-notification-off-line text-gray-500 text-lg"></i>
                </div>
                <p className="text-xs text-gray-500">Nenhuma notificação</p>
              </div>
            ) : (
              notifications.map((n) => {
                const icon = ICON_MAP[n.type] || ICON_MAP.default;
                const color = COLOR_MAP[n.type] || COLOR_MAP.default;
                return (
                  <div
                    key={n.id}
                    onClick={() => handleNotifClick(n)}
                    className={`flex items-start gap-3 px-4 py-3 hover:bg-white/3 transition-all cursor-pointer ${!n.is_read ? 'bg-[#D4AF37]/3' : ''}`}
                  >
                    <div className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 mt-0.5 ${!n.is_read ? 'bg-[#D4AF37]/10' : 'bg-white/5'}`}>
                      <i className={`${icon} text-sm ${color}`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs leading-relaxed ${!n.is_read ? 'text-[#F5F5F0] font-semibold' : 'text-gray-300'}`}>
                        {n.title}
                      </p>
                      {n.body && (
                        <p className="text-[10px] text-gray-500 mt-0.5 line-clamp-2">{n.body}</p>
                      )}
                      <p className="text-[10px] text-gray-600 mt-1">{timeAgo(n.created_at)}</p>
                    </div>
                    {!n.is_read && (
                      <div className="w-2 h-2 bg-[#D4AF37] rounded-full shrink-0 mt-1.5"></div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2.5 border-t border-white/[0.08]">
              <button
                onClick={() => setOpen(false)}
                className="w-full text-center text-xs text-gray-500 hover:text-[#D4AF37] transition-colors cursor-pointer"
              >
                Fechar
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
